import { LOCALES } from '@aysar/contracts';

import { readPath, writePath } from '../build-default-values/build-default-values';
import { TRANSLATIONS_KEY, localizedPath } from '../localized-form/localized-form';
import { type ResolvedSection } from '../resolve-sections/resolve-sections';
import { type FieldDef } from '../types/dynamic-form.types';

/** Whether a field is on screen for the given form values. */
export function isFieldVisible(field: FieldDef, values: Record<string, unknown>): boolean {
  return field.visibleWhen === undefined || field.visibleWhen(values);
}

/** Whether a section is on screen for the given form values. */
export function isSectionVisible(
  section: ResolvedSection,
  values: Record<string, unknown>,
): boolean {
  return section.visibleWhen === undefined || section.visibleWhen(values);
}

/**
 * Removes one dotted path, cloning every object it passes through.
 *
 * A field name is a **path**, not a key — an object group's properties are
 * `value.country` — and `delete out['value.country']` removes a key nothing ever
 * had while leaving the real value in place, so a hidden property would still be
 * submitted. Cloning on the way down is what keeps RHF's own live values
 * untouched, exactly as the shallow copy at the top of `stripFields` does for
 * the first level.
 *
 * A branch that is not a plain object is left alone rather than replaced: the
 * form holds what it holds, and inventing a shape to delete from would be a
 * write dressed up as a removal.
 */
function deletePath(target: Record<string, unknown>, path: string): void {
  const segments = path.split('.');
  const leaf = segments.pop();

  if (leaf === undefined) return;

  let cursor = target;

  for (const segment of segments) {
    const next = cursor[segment];
    if (typeof next !== 'object' || next === null || Array.isArray(next)) return;

    const copy: Record<string, unknown> = { ...(next as Record<string, unknown>) };
    cursor[segment] = copy;
    cursor = copy;
  }

  delete cursor[leaf];
}

/**
 * Removes the values of the named fields, translatable ones in every locale —
 * or writes `null` in their place for a field that declared `isHiddenNull`.
 *
 * The shared half of both strips below — what is hidden is treated identically
 * whether it was the field or the group around it that hid it.
 */
function stripFields(
  hidden: readonly FieldDef[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  if (hidden.length === 0) return values;

  const out: Record<string, unknown> = { ...values };
  const translations = out[TRANSLATIONS_KEY];
  const nextTranslations: Record<string, unknown> | null =
    typeof translations === 'object' && translations !== null && !Array.isArray(translations)
      ? { ...(translations as Record<string, unknown>) }
      : null;

  for (const field of hidden) {
    const asNull = field.isHiddenNull === true;

    if (field.isTranslatable === true) {
      if (nextTranslations === null) continue;

      for (const locale of LOCALES) {
        const perLocale = nextTranslations[locale];
        if (typeof perLocale !== 'object' || perLocale === null) continue;

        const copy: Record<string, unknown> = { ...(perLocale as Record<string, unknown>) };
        if (asNull) {
          copy[field.name] = null;
        } else {
          delete copy[field.name];
        }
        nextTranslations[locale] = copy;
      }
      continue;
    }

    if (asNull) {
      writePath(out, field.name, null);
    } else {
      deletePath(out, field.name);
    }
  }

  if (nextTranslations !== null) {
    out[TRANSLATIONS_KEY] = nextTranslations;
  }

  return out;
}

/**
 * The same strip, one level up: everything inside a section the form is not
 * showing.
 *
 * A `.strict()` contract schema cannot tell a value left behind by a hidden
 * FIELD from one left behind by a hidden GROUP, so a section predicate without
 * this is the dead-Save defect below with a bigger blast radius.
 */
export function stripHiddenSections(
  sections: readonly ResolvedSection[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  return stripFields(
    sections
      .filter((section) => !isSectionVisible(section, values))
      .flatMap((section) => [...section.fields]),
    values,
  );
}

/**
 * Removes the values of every field `visibleWhen` currently hides.
 *
 * Run **before** validation rather than at submit, and that ordering is the whole
 * point. A question whose type moves from `number` to `text` leaves `min` behind
 * in the form; the contract's `TextValidationSchema` is `.strict()`, so a lingering
 * `min` fails validation on a field the operator cannot see and Save becomes a
 * dead button with the error attached to nothing. Stripping first means the form
 * submits exactly what is on screen.
 *
 * A translatable field is stripped in **every** locale: it is hidden as a field,
 * not as a tab.
 *
 * Returns a shallow copy — the caller's object is never mutated, because RHF hands
 * over its own live values.
 */
export function stripHiddenFields(
  fields: readonly FieldDef[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  return stripFields(
    fields.filter((field) => !isFieldVisible(field, values)),
    values,
  );
}

/**
 * Resolves what an empty box means for the fields that declared one.
 *
 * Beside `stripHiddenFields` and for the same reason: the resolver's returned
 * values are what RHF hands the submit handler, so acting here covers the
 * schema AND the payload. Doing it at submit alone would leave `''` to fail an
 * optional field's own minimum, with the error landing on a control the operator
 * never touched.
 *
 * **Two answers, because contracts ask for two.** `isEmptyAbsent` removes the
 * key — for a field that is `.optional()` and carries a minimum. `isEmptyNull`
 * writes `null` — for one that is `.nullable()` and **required**, where removing
 * the key fails `.strict()` with "Required" on a control the operator emptied on
 * purpose. Absence wins when both are declared: it is the more conservative
 * write.
 */
export function stripEmptyFields(
  fields: readonly FieldDef[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  let stripped = values;

  for (const field of fields) {
    if (field.kind === 'object') continue;

    const asAbsent = field.isEmptyAbsent === true;
    if (!asAbsent && field.isEmptyNull !== true) continue;

    // Every path the field occupies: a translatable one is `translations.<locale>.<name>`
    // in each locale, and a plain one may still be a DOTTED absolute path (an
    // object group's property). Reading `values[field.name]` handled neither, so
    // the flag was a silent no-op on both — a dead flag with no signal.
    const paths =
      field.isTranslatable === true
        ? LOCALES.map((locale) => localizedPath(field.name, locale))
        : [field.name];

    for (const path of paths) {
      if (readPath(stripped, path) !== '') continue;

      // Copied once, lazily: a form with nothing to resolve is handed back
      // untouched rather than shallow-cloned for nothing.
      if (stripped === values) stripped = { ...values };

      if (asAbsent) {
        deletePath(stripped, path);
      } else {
        // `writePath` clones every object it passes through, exactly as
        // `deletePath` does — RHF hands over its own live values and neither
        // may mutate them.
        writePath(stripped, path, null);
      }
    }
  }

  return stripped;
}
